// Utilitários do modelo "Larissa" (topo 14 · busca 17 · card 11 · rodapé 11) — linguagem visual de um
// site de referência (monocromático, barra sólida escura, serifa Lora nos títulos + Inter no corpo,
// cards brancos de raio 16 com sombra longa no hover) reproduzida em CSS modules NOSSOS, com as
// CORES DO TENANT. Só as 4 peças do Larissa importam daqui — não é componente compartilhado dos
// ~300 sites. AUTOSSUFICIENTE: não depende das peças do Panorama (modelo #10), só dos helpers
// genéricos do Horizonte/Atelier (contatos, menu, redes, WhatsApp, URL/foto do imóvel).
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { ensureReadableTextColor, getContrastRatio, isColorLight, NumeroFormatado, toPrice } from '../../../utils/FunctionsGlobals';
import { hexParaRgba, misturarCores } from '../horizonte/horizonte';
import type { FormatadorPreco } from '../../../utils/moedaSite';

export { contatosDoTenant, hexParaRgba, linkWhatsApp, linkWhatsAppDoTenant, menuDoTenant, mesmaRota, misturarCores, redesDoTenant, useMovimentoReduzido } from '../horizonte/horizonte';
export type { ItemMenu, TelefoneTenant } from '../horizonte/horizonte';
export { fotoOtimizada, fotosDoImovel, nomeDaEmpresa, stripHtml, urlDoImovel } from '../atelier/atelier';

// Curva das entradas do modelo (desacelera longo no fim) — a mesma dos modelos 8/9/10.
export const EASE_LARISSA = [0.22, 1, 0.36, 1] as const;

/** Escala de RESERVA (tokens do site de referência): entra só onde o tenant não tem cor utilizável. */
export const RESERVA = {
    base: '#1d1d1f',
    hover: '#151517',
    cinza: '#86868b',
    bordaForte: '#d2d2d7',
    borda: '#e8e8ed',
    fundo: '#f5f5f7',
};

export interface PaletaLarissa {
    /** cor escura do modelo: topo, rodapé e botões (texto branco lê ≥ 5.5:1). */
    base: string;
    /** base mais funda (hover dos botões, painel do menu). */
    hover: string;
    /** texto principal sobre branco (a base escurecida até ler ≥ 12:1). */
    tinta: string;
    /** texto secundário sobre branco/fundo claro (≥ 4.5:1). */
    cinza: string;
    /** borda de foco / divisória mais marcada. */
    bordaForte: string;
    /** bordas e divisórias. */
    borda: string;
    /** fundo claro da página/seções e dos campos. */
    fundo: string;
    /** texto secundário SOBRE a base escura (≥ 4.5:1). */
    sobreBase: string;
}

export const hexValido = (v: unknown): string => {
    let h = String(v ?? '')
        .trim()
        .toLowerCase();
    if (!h) return '';
    if (!h.startsWith('#')) h = `#${h}`;
    if (/^#[0-9a-f]{3}$/.test(h)) h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
    return /^#[0-9a-f]{6}$/.test(h) ? h : '';
};

/** Saturação HSL (0–1). */
const saturacao = (hex: string): number => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    if (max === min) return 0;
    const l = (max + min) / 2;
    const d = max - min;
    return l > 0.5 ? d / (2 - max - min) : d / (max + min);
};
const temCor = (hex: string): boolean => Boolean(hex) && saturacao(hex) >= 0.18;
/** luminância relativa (0–1) via contraste com o preto. */
export const luminancia = (hex: string): number => getContrastRatio(hex, '#000000') * 0.05 - 0.05;
/** preto/grafite (sem saturação, bem escuro) também serve de base — só branco/cinza claro não serve. */
const neutraEscura = (hex: string): boolean => Boolean(hex) && saturacao(hex) < 0.18 && luminancia(hex) < 0.12;
export const serveDeBase = (hex: string): boolean => temCor(hex) || neutraEscura(hex);

/** Puxa `cor` para `alvo` até `texto` ler ≥ `minimo` sobre ela (passos de 5%). */
export const ajustarAte = (cor: string, alvo: string, texto: string, minimo: number): string => {
    for (let t = 0; t <= 1.0001; t += 0.05) {
        const c = t === 0 ? cor : misturarCores(cor, alvo, t);
        if (getContrastRatio(texto, c) >= minimo) return c;
    }
    return alvo;
};

/**
 * Mapeamento p/ os 300 sites (mesma regra do modelo #10): base = a cor MAIS ESCURA do tenant que serve
 * (saturada ou preto/grafite; cor1 em empate, nunca branco/cinza claro), escurecida até texto branco
 * ler ≥ 5.5:1 (folga p/ textos a 90% e placas translúcidas lerem AA). A referência é MONOCROMÁTICA: não há cor de destaque — cinzas, bordas e fundos são a
 * escala neutra da referência com uma pitada da base. Sem cor no tenant → grafite de reserva.
 */
export const paletaLarissa = (site: any): PaletaLarissa => {
    const cor1 = hexValido(site?.cor1);
    const cor2 = hexValido(site?.cor2);
    const bases = [cor1, cor2].filter(serveDeBase);
    // a cor2 só toma o lugar da principal quando é CLARAMENTE mais escura
    const bruta = bases.length === 2 && luminancia(cor2) < luminancia(cor1) * 0.6 ? cor2 : bases[0] ?? '';

    // 5.5 (não 4.5): sobre a base vão textos brancos a 90% e placas `rgba(255,255,255,.1)` — com 4.5 cravado
    // o menu do topo e o telefone do rodapé liam 4.0–4.4:1 nos tenants de cor média (cs2059, verde).
    const base = bruta ? ajustarAte(bruta, '#0b0b0d', '#ffffff', 5.5) : RESERVA.base;
    const hover = bruta ? misturarCores(base, '#000000', 0.28) : RESERVA.hover;
    // texto sobre branco: a própria base quando já é bem escura; senão escurece (um azul médio vira marinho)
    const tinta = ajustarAte(base, '#0b0b0d', '#ffffff', 12);
    const cinzaBruto = bruta ? misturarCores(RESERVA.cinza, base, 0.08) : RESERVA.cinza;
    // secundário precisa ler 4.5:1 sobre o FUNDO CLARO da seção (o cinza da referência lê 3.6:1)
    const fundo = bruta ? misturarCores(RESERVA.fundo, base, 0.02) : RESERVA.fundo;
    const cinza = (() => {
        for (let t = 0; t <= 1.0001; t += 0.05) {
            // escurece rumo ao GRAFITE (neutro), não à tinta: a referência é monocromática
            const c = t === 0 ? cinzaBruto : misturarCores(cinzaBruto, RESERVA.base, t);
            if (getContrastRatio(c, fundo) >= 4.5) return c;
        }
        return tinta;
    })();
    const sobreBase = (() => {
        const partida = misturarCores(base, '#ffffff', 0.6);
        for (let t = 0; t <= 1.0001; t += 0.05) {
            const c = t === 0 ? partida : misturarCores(partida, '#ffffff', t);
            if (getContrastRatio(c, base) >= 4.5) return c;
        }
        return '#ffffff';
    })();

    return {
        base,
        hover,
        tinta,
        cinza,
        bordaForte: bruta ? misturarCores(RESERVA.bordaForte, base, 0.06) : RESERVA.bordaForte,
        borda: bruta ? misturarCores(RESERVA.borda, base, 0.04) : RESERVA.borda,
        fundo,
        sobreBase,
    };
};

const lumDoPixel = (r: number, g: number, b: number): number => {
    const f = (v: number) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
/**
 * Logo CLARA (branca/dourada, feita para fundo escuro) some na placa branca do topo e do rodapé (cs1722: logo
 * 100% branca = quadrado vazio). Depois de montar, lê os pixels opacos da logo (a CDN manda CORS `*`): se ≥ 90%
 * deles leem ≥ 2:1 sobre a BASE escura e menos de 75% leem sobre o branco, a logo vai direto sobre a base, sem
 * placa. Logo escura, de dois tons, sem transparência ou ilegível para o canvas (CORS/erro) → placa, como no SSR.
 */
export const useLogoSemPlaca = (src: string, base: string): boolean => {
    const [semPlaca, setSemPlaca] = useState(false);
    useEffect(() => {
        setSemPlaca(false);
        if (!src || typeof window === 'undefined') return;
        let vivo = true;
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        img.onload = () => {
            try {
                const lado = 48;
                const tela = document.createElement('canvas');
                tela.width = lado;
                tela.height = lado;
                const ctx = tela.getContext('2d', { willReadFrequently: true });
                if (!ctx) return;
                ctx.drawImage(img, 0, 0, lado, lado);
                const d = ctx.getImageData(0, 0, lado, lado).data;
                const lumBase = luminancia(base);
                let opacos = 0;
                let leSobreBase = 0;
                let leSobreBranco = 0;
                for (let i = 0; i < d.length; i += 4) {
                    if (d[i + 3] < 128) continue;
                    opacos++;
                    const l = lumDoPixel(d[i], d[i + 1], d[i + 2]);
                    if ((Math.max(l, lumBase) + 0.05) / (Math.min(l, lumBase) + 0.05) >= 2) leSobreBase++;
                    if (1.05 / (l + 0.05) >= 2) leSobreBranco++;
                }
                // sem transparência a imagem é um retângulo: tirar a placa não muda nada
                if (!opacos || opacos / (lado * lado) > 0.9) return;
                if (vivo && leSobreBase / opacos >= 0.9 && leSobreBranco / opacos < 0.75) setSemPlaca(true);
            } catch {
                /* canvas sem leitura (CORS): fica a placa */
            }
        };
        img.src = src;
        return () => {
            vivo = false;
        };
    }, [src, base]);
    return semPlaca;
};

/** Variáveis CSS (--lr-*) que os 4 módulos consomem; o Builder pode sobrescrevê-las por CSS custom. */
export const varsLarissa = (site: any): CSSProperties => {
    const p = paletaLarissa(site);
    return {
        '--lr-base': p.base,
        '--lr-hover': p.hover,
        '--lr-tinta': p.tinta,
        '--lr-cinza': p.cinza,
        '--lr-borda-forte': p.bordaForte,
        '--lr-borda': p.borda,
        '--lr-fundo': p.fundo,
        '--lr-sobre-base': p.sobreBase,
        '--lr-sombra': hexParaRgba(p.base, 0.12, 'rgba(29, 29, 31, ALPHA)'),
        '--lr-base-veu': hexParaRgba(p.base, 0.55, 'rgba(29, 29, 31, ALPHA)'),
    } as CSSProperties;
};

/** Gate do modelo #11 "Larissa" = topo 14 (mesmo critério de ehModeloHorizonte/ehModeloPanorama). */
export const ehModeloLarissa = (site: any): boolean => Number(site?.modelo_topo) === 14;

/**
 * Páginas internas no visual do modelo (/imoveis, ficha do imóvel, /condominios, /empreendimento) — o
 * mesmo esquema de varsHorizonteInterna: as vars do modelo + o que a PÁGINA precisa. Texto legível
 * sobre a cor5 do tenant (fundo claro → tinta do modelo; escuro → cor13), tons "de página" derivados
 * desse texto (leem em fundo claro E escuro), gutter comum dos blocos e Inter na página toda (o
 * mecanismo das peças do modelo: redefinir --fonte-principal na raiz). Fora do topo 14: null ⇒ nada muda.
 */
export const varsLarissaInterna = (site: any): CSSProperties | null => {
    if (!ehModeloLarissa(site)) return null;
    const p = paletaLarissa(site);
    const fundo = hexValido(site?.cor5) || '#ffffff';
    const claro = isColorLight(fundo);
    const texto = claro ? ensureReadableTextColor(p.tinta, fundo) : ensureReadableTextColor(site?.cor13, fundo);
    const reserva = claro ? 'rgba(29, 29, 31, ALPHA)' : 'rgba(255, 255, 255, ALPHA)';
    return {
        ...varsLarissa(site),
        color: texto,
        '--fonte-principal': "'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        '--ap-gutter': 'clamp(16px, 4vw, 56px)',
        '--lr-fundo-pagina': fundo,
        '--lr-texto': texto,
        '--lr-texto-suave': claro ? ensureReadableTextColor(p.cinza, fundo) : hexParaRgba(texto, 0.72, reserva),
        '--lr-sutil': hexParaRgba(texto, 0.12, reserva),
        '--lr-sutil-forte': hexParaRgba(texto, 0.24, reserva),
        '--lr-painel': claro ? '#ffffff' : hexParaRgba(texto, 0.06, reserva),
    } as CSSProperties;
};

// ---------------------------------------------------------------------------
// Imóvel: atributos em linha ("586 m² • 5 dorms • 3 suítes • 10 vagas") e preços ROTULADOS.
// ---------------------------------------------------------------------------

const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;

/** Até 4 atributos com valor > 0, na ordem da referência (área, dorms, suítes, vagas). */
export const atributosDoImovel = (slide: any, maximo = 4): string[] => {
    const n = (v: any) => Number(v) || 0;
    const itens: string[] = [];
    const area = slide?.area_privativa > 0 ? slide.area_privativa : slide?.area_total > 0 ? slide.area_total : slide?.terreno > 0 ? slide.terreno : 0;
    if (area > 0) itens.push(`${NumeroFormatado(Number(area))} ${slide?.area_privativa > 0 || slide?.area_total > 0 ? 'm²' : slide?.area_tipo || 'm²'}`);
    if (n(slide?.dormitorios) > 0) itens.push(plural(n(slide.dormitorios), 'dorm', 'dorms'));
    if (n(slide?.suites) > 0) itens.push(plural(n(slide.suites), 'suíte', 'suítes'));
    if (n(slide?.garagems) > 0) itens.push(plural(n(slide.garagems), 'vaga', 'vagas'));
    if (itens.length < maximo && n(slide?.banheiros) > 0) itens.push(plural(n(slide.banheiros), 'banheiro', 'banheiros'));
    return itens.slice(0, maximo);
};

export type PrecoRotulado = { rotulo: string; valor: string; consulta?: boolean };

/** "Venda:" / "Locação:" com as MESMAS regras de exibição dos cards existentes. */
export const precosRotulados = (slide: any, site: any, fmt?: FormatadorPreco): PrecoRotulado[] => {
    // 21/10: `fmt` (usePrecoMoeda) converte para a moeda do idioma; sem ele, o texto de sempre.
    const moeda = (preco: number) => {
        const simbolo = slide?.moeda_imovel && slide?.moeda_imovel != site?.moeda_tipo ? slide?.moeda_imovel : site?.moeda_tipo || 'R$';
        const original = `${simbolo} ${toPrice(preco, 2)}`;
        return fmt ? fmt(preco, simbolo, original) : original;
    };
    const showVenda = Boolean(slide?.exibir_preco_venda && slide?.preco_venda > 0);
    const showLocacao = Boolean(slide?.aluguel > 0 && slide?.exibir_preco_locacao && slide?.preco_locacao > 0);
    const out: PrecoRotulado[] = [];
    if (showVenda) out.push({ rotulo: 'Venda', valor: moeda(slide.preco_venda) });
    else if (slide?.venda == 1) out.push({ rotulo: 'Venda', valor: 'Consulte', consulta: true });
    if (showLocacao) out.push({ rotulo: 'Locação', valor: moeda(slide.preco_locacao) });
    else if (slide?.aluguel == 1 && !out.length) out.push({ rotulo: 'Locação', valor: 'Consulte', consulta: true });
    return out.slice(0, 2);
};

/** Nome do card (serifa): o BAIRRO, como na referência; sem bairro, a cidade; sem ambos, o tipo. */
export const lugarDoImovel = (slide: any): string => String(slide?.bairro?.nome || slide?.cidade?.nome || slide?.tipo?.nome || 'Imóvel').trim();

/**
 * Parte um título em duas tonalidades (forte + suave), como os títulos serifados da referência:
 * corta no 1º sinal (":" "—" "–" "," "|"); sem sinal e com 4+ palavras, corta ao meio. Título curto fica inteiro.
 */
export const tituloEmDoisTons = (texto: unknown): { forte: string; suave: string } => {
    const t = String(texto ?? '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!t) return { forte: '', suave: '' };
    const m = /^(.{3,}?)(\s*[:—–|]\s*|,\s+)(.{3,})$/.exec(t);
    if (m) return { forte: `${m[1]}${m[2].trim() === ':' ? ':' : m[2].trim() === ',' ? ',' : ''}`, suave: m[3] };
    const palavras = t.split(' ');
    if (palavras.length < 4) return { forte: t, suave: '' };
    const corte = Math.ceil(palavras.length / 2);
    return { forte: palavras.slice(0, corte).join(' '), suave: palavras.slice(corte).join(' ') };
};

/** "DESTAQUES DE VENDAS" → "Destaques de vendas" (títulos de carrossel cadastrados em caixa alta ficam ruins em serifa). */
export const semCaixaAlta = (texto: unknown): string => {
    const t = String(texto ?? '').trim();
    if (!t || t !== t.toUpperCase() || t === t.toLowerCase()) return t;
    const baixo = t.toLowerCase();
    return baixo.charAt(0).toUpperCase() + baixo.slice(1);
};
